import * as React from 'react';
import styled from 'styled-components';
import { ViewState } from 'react-map-gl';

import { AnimatedLoader } from "@independent-software/mapbox-ext/controls/AnimatedLoader";

import { AquaMapControl } from './controls/AquaMapControl';
import { AquaMapButtonGroup } from './controls/AquaMapButtonGroup';
import { AquaCompassButton, AquaStyleButton, AquaZoomInButton, AquaZoomOutButton } from './controls/AquaMapButtons';
import { AquaLegend } from './panels/AquaLegend';
import { AquaCoordinates } from './panels/AquaCoordinates';
import { AquaScale } from './panels/AquaScale';
import { Config } from '../../Config';
import { Guide } from '../guide/Guide';
import { ILngLat } from '../util/MousePosition';

// Gap between controls and the map edges, and between controls, in pixels.
const GAP = 12;
// Map button size, in pixels.
const BUTTON = 32;
// Width of the open layer menu, in pixels.
const MENU_WIDTH = 400;
// Space kept clear at the bottom for the Mapbox logo, in pixels.
const BOTTOM = 36;
// The guide's own offset from the right (GuideBox): clear of the menu button.
const GUIDE_RIGHT = 68;

interface IProps {
  /** Current map view. */
  viewState: ViewState;
  /** Is the layer menu open? */
  open: boolean;
  /** Is satellite imagery shown? */
  satellite: boolean;
  /** Is any data loading? */
  loading: boolean;
  /** Placed marker, if any. Otherwise the coordinates panel follows the mouse. */
  marker: ILngLat | null;
  /** Fired when the satellite button is clicked. */
  onToggleSatellite: () => void;
}

/**
 * The Aqua map controls: AquaUI map buttons in the bottom-right corner, with
 * GlassPane legend and coordinates panels to their left. Everything shifts
 * left when the layer menu opens.
 */
const AquaMapControls = (props: IProps) => {
  const v = props.viewState;
  const right = -(props.open ? MENU_WIDTH : GAP);
  const panelsRight = right - BUTTON - GAP;

  const buttons = (x: number) => <>
    {/* Show style switch only if a secondary style is available: */}
    {Config.map.greyStyle &&
      <AquaStyleButton x={x} y={-(BOTTOM + 3 * BUTTON + 2 + GAP)} active={props.satellite} onClick={props.onToggleSatellite}/>}
    <AquaMapButtonGroup x={x} y={-BOTTOM}>
      <AquaZoomInButton zoom={v.zoom}/>
      <AquaZoomOutButton zoom={v.zoom}/>
      <AquaCompassButton zoom={v.zoom} bearing={v.bearing} pitch={v.pitch}/>
    </AquaMapButtonGroup>
  </>;

  return <>
    <LargeScreen>
      {buttons(right)}
      <AquaMapControl x={panelsRight} y={-BOTTOM}>
        <Stack>
          {Config.suitability &&
            <AquaLegend caption={Config.legend_caption} entries={Config.suitability.filter(s => !s.hidden)}/>}
          <AquaCoordinates marker={props.marker}/>
          <AquaScale latitude={v.latitude} zoom={v.zoom} width={120}/>
        </Stack>
      </AquaMapControl>
      <AnimatedLoader x={panelsRight - 170 - GAP} y={-BOTTOM} active={props.loading}/>
      {/* The guide moves left with the other panels when the menu opens, so
          it can still be read while operating the menu. Clicks on the guide
          must not reach Main, which would close the menu. */}
      <GuideDock style={{ right: props.open ? MENU_WIDTH - GUIDE_RIGHT : 0 }} onClick={e => e.stopPropagation()}>
        <Guide/>
      </GuideDock>
    </LargeScreen>
    <SmallScreen>
      {buttons(-GAP)}
      <AnimatedLoader x={-(GAP + BUTTON + GAP)} y={-BOTTOM} active={props.loading}/>
    </SmallScreen>
  </>;
}

const GuideDock = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  transition: right ease-out 200ms;
  & > * {
    pointer-events: auto;
  }
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LargeScreen = styled.div`
  @media not (min-width: ${Config.UI.large_screen}px) {
    display: none;
  }
`

const SmallScreen = styled.div`
  @media (min-width: ${Config.UI.large_screen}px) {
    display: none;
  }
`

export { AquaMapControls }
